import { eventBus } from '_/helpers/eventBus';

export default {
    /**
     * Define props
     *
     * @type  {Object}
     */
    props: {
        defaultValue: {
            type: [Boolean, String],
        },
        disabled: {
            type: Boolean,
        },
        fieldType: {
            type: String,
        },
        id: {
            required: true,
            type: [Number, String],
        },
        name: {
            required: true,
            type: String,
        },
        placeholder: {
            type: String,
        },
        refreshFields: {
            type: Number,
        },
        required: {
            type: Boolean,
        },
        value: {
            default: null,
            type: [Number, String],
        },
        focus: {
            default: null,
            type: Boolean,
        },
    },

    /**
     * Set the methods
     *
     * @type  {Object}
     */
    methods: {
        /**
         * Creates the event payload
         *
         * @param   {object}  event
         * @return  {object}
         */
        createEventPayload(event) {
            const timestamp = event.date ? event.date.valueOf() : null;

            let value = '';

            if (timestamp) {
                if (this.isDateTime()) {
                    // UTC with an explicit +00:00 offset (M2-122): the server keeps
                    // date-times in UTC. MySQL 8 converts any offset to the session
                    // zone (UTC) but MariaDB drops it, so a browser-local "+02:00"
                    // was stored as local wall-clock time on MariaDB only.
                    value = moment(timestamp).utc().format('YYYY-MM-DDTHH:mm:ssZ');
                } else {
                    value = moment(timestamp).format('YYYY-MM-DD');
                }
            }

            return {
                event,
                id: this.id,
                name: this.name,
                value,
            };
        },

        /**
         * Computes the date format using full formatting (including hours, minutes and seconds etc.)
         *
         * @param   {object}  date
         * @return  {string}
         */
        dateWithHoursMinutesSeconds(humanReadable = false) {
            if (this.value) {
                return humanReadable
                    ? this.localMoment().format('DD MMMM YYYY - HH:mm')
                    : this.localMoment().format('YYYY-MM-DD HH-mm-ssZ');
            }

            return null;
        },

        /**
         * The stored value as browser-local time (M2-122).
         *
         * Photon returns date-time fields zone-less ("2026-09-29 08:53:00") in
         * server time, which is UTC; reading them with plain moment() showed UTC
         * wall-clock time as if it were local, two hours early in CEST. Values
         * with an offset ("…Z", "…+00:00" from createEventPayload) parse the same.
         * Date-only fields have no time to shift and are left alone.
         *
         * @return  {object}
         */
        localMoment() {
            return this.isDateTime() ? moment.utc(this.value).local() : moment(this.value);
        },

        /**
         * Date-time vs date-only. System Date-time (Approved At, Partner Since …)
         * carries a time too; core rendered it date-only although the picker was
         * already set up with hours and minutes for it (M2-122).
         *
         * @return  {boolean}
         */
        isDateTime() {
            return this.fieldType === 'DateTime' || this.fieldType === 'SystemDateTime';
        },

        /**
         * Computes the date format using only year, month and day
         *
         * @return  {string}
         */
        dateWithoutHoursMinutesSeconds(humanReadable = false) {
            if (this.value) {
                return humanReadable
                    ? this.localMoment().format('DD MMMM YYYY')
                    : this.localMoment().format('YYYY-MM-DD');
            }

            return null;
        },

        /**
         * Initializes the datetimepicker plug-in
         *
         * @return  {void}
         */
        initializeDatetimepicker() {
            if (this.disabled) {
                return;
            }

            const calendarField = $(this.$el).find(`#${this.id}-container`);

            let dateTimePickerOptions = {
                autoclose: true,
                fontAwesome: true,
                maxView: 'year',
                todayBtn: true,
            };

            if (this.fieldType === 'DateTime' || this.fieldType === 'SystemDateTime') {
                dateTimePickerOptions.format = 'dd MM yyyy - hh:ii';

                dateTimePickerOptions.linkFormat = 'yyyy-mm-ddThh:ii:ssZ';

                dateTimePickerOptions.minView = 'hour';
            } else {
                dateTimePickerOptions.format = 'dd MM yyyy';

                dateTimePickerOptions.linkFormat = 'yyyy-mm-dd';

                dateTimePickerOptions.minView = 'month';
            }

            calendarField.datetimepicker(dateTimePickerOptions);

            calendarField.off('changeDate');

            calendarField.on('changeDate', (event) => {
                this.onChange(event);
            });
        },

        /**
         * Emmits a change event with a payload consting of input value and the event object
         *
         * @param   {event}  event
         * @return  {void}
         */
        onChange(event) {
            this.$emit('change', this.createEventPayload(event));

            eventBus.$emit('fieldTypeChange', this.createEventPayload(event));
        },

        /**
         * Sets a component value to defaultValue, if required
         *
         * @return  {void}
         */
        setDefaultValueIfRequired() {
            if (this.value === null && this.defaultValue) {
                this.onChange({
                    date: this.defaultValue,
                });
            }
        },
    },

    computed: {
        /**
         * Computes the date format according to the field type
         *
         * @return  {string}
         */
        date() {
            if (this.isDateTime()) {
                return this.dateWithHoursMinutesSeconds();
            }

            return this.dateWithoutHoursMinutesSeconds();
        },

        /**
         * Computes the human readable date format according to the field type
         *
         * @return  {string}
         */
        humanReadableDate() {
            if (this.isDateTime()) {
                return this.dateWithHoursMinutesSeconds(true);
            }

            return this.dateWithoutHoursMinutesSeconds(true);
        },

        /**
         * Handles the visibility of remove button
         *
         * @return  {boolean}
         */
        shouldShowRemoveButton() {
            if (this.required) {
                return false;
            }

            if (this.disabled) {
                return false;
            }

            return true;
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.setDefaultValueIfRequired();

            this.initializeDatetimepicker();
            if (this.focus) {
                $(this.$el).find(`#${this.id}`).focus();
            }
        });
    },

    beforeDestroy() {
        $(this.$el).find(`#${this.id}-container`).datetimepicker('remove');
    },

    watch: {
        refreshFields(newEntry, oldEntry) {
            if (newEntry !== oldEntry) {
                this.setDefaultValueIfRequired();

                this.$forceUpdate();

                $(this.$el).find(`#${this.id}-container`).datetimepicker('remove');

                this.initializeDatetimepicker();

                if (this.focus) {
                    $(this.$el).find(`#${this.id}`).focus();
                }
            }
        },
    },
};
